import { Image, Video } from "lucide-react";

import type { TemplateCard } from "../api/generated/model";
import { CoverVideo } from "./CoverVideo";

/** The fields of a template card its cover reads. */
export type TemplateCoverFields = Pick<TemplateCard, "media" | "cover_url" | "cover_poster_url" | "cover_card_url">;

/** What a template card's cover shows: its clip over its poster, a still, or its type's icon. */
export type TemplateCoverPlan =
  | { kind: "clip"; clip: string; poster: string | null }
  | { kind: "still"; src: string }
  | { kind: "icon" };

/**
 * A video template's card rendition (`cover_card_url`, #828 bug 17) over its first-frame poster,
 * else that poster as a still: never the cover's original, live's file, which only a large view
 * shows. An image template's cover. With neither, the type's icon, as live's card with no preview.
 */
export function templateCover(template: TemplateCoverFields): TemplateCoverPlan {
  if (template.media === "video") {
    const clip = template.cover_card_url ?? null;
    const poster = template.cover_poster_url ?? null;
    if (clip !== null) return { kind: "clip", clip, poster };
    return poster === null ? { kind: "icon" } : { kind: "still", src: poster };
  }
  return template.cover_url === null ? { kind: "icon" } : { kind: "still", src: template.cover_url };
}

export interface TemplateCoverProps {
  template: TemplateCoverFields;
  /** Each layer's (the still, the clip): it fills the card. */
  className?: string | undefined;
  /** The box of the type's icon, on a card with no cover. */
  iconClassName?: string | undefined;
  /** The cover can be shown: its still or poster has loaded, or failed. The icon has none to wait for. */
  onReady?: (() => void) | undefined;
}

/**
 * A template card's cover, the same on every card that lists templates: Classic's catalog and
 * Pro's Home, Templates page and Simple's step (spec ui-perf/template-card-previews). A clip
 * plays muted on a loop while its card is on screen (`CoverVideo`); a still loads as the card
 * nears the view.
 */
export function TemplateCover({ template, className, iconClassName, onReady }: TemplateCoverProps) {
  const plan = templateCover(template);
  if (plan.kind === "clip") {
    return <CoverVideo className={className} src={plan.clip} poster={plan.poster} onReady={onReady} />;
  }
  if (plan.kind === "still") {
    return (
      <img
        className={className}
        src={plan.src}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={onReady}
        onError={onReady}
      />
    );
  }
  const Icon = template.media === "video" ? Video : Image;
  return (
    <span className={iconClassName}>
      <Icon size={32} strokeWidth={1.5} aria-hidden />
    </span>
  );
}
